import { defaultLayer, } from '@strength/db-schema';
import { WorkoutLayerDomain } from '@strength/domain';
import { nextSortOrder } from '@strength/utils';
import { Layer } from '../layer-accessor';
import { v4 } from 'uuid';
/**
 * Low-level API for building workout layer edits.
 *
 * Accumulates mutations (add/edit/remove sections, exercises, supersets, etc.) and returns
 * an array of edits via `getEdits()`. Handles cascading operations (e.g., removing an exercise
 * also removes its sets, metrics, and set metrics).
 *
 * This builder creates edits based on the target layer that is passed in. This will not handle all business logic cases.
 * That is where we utilize these functions to create functions that handle cross layer logic like in workout-draft.ts
 *
 * These methods should be heavily unit tested as they directly manipulate the workout layer.
 *
 * For common workflows, use workout-draft functions which wrap WorkoutLayerBuilder methods
 * into higher-level operations. Some convenience methods like `copyEntriesToSection` exist
 * directly on the builder due to complexity and frequent use.
 */
export class WorkoutLayerBuilder {
    layer;
    edits = [];
    /** Initialize a new WorkoutLayerBuilder with optional base layer */
    constructor(options) {
        const { workoutLayer } = options || {};
        const layer = workoutLayer
            ? 'layer' in workoutLayer
                ? workoutLayer.layer
                : workoutLayer
            : null;
        // Initialize with baseWorkout or empty arrays using defaultLayer helper
        this.layer = defaultLayer(layer ?? undefined);
    }
    /**
     * Clears all sections from a workout, effectively resetting it to empty.
     *
     * This method handles two scenarios:
     * 1. Base layer workouts: Removes all sections in the current layer
     * 2. Athlete layer workouts: Removes all sections AND creates removal edits for any
     *    sections from the base layer that don't already exist in the athlete layer.
     *    This ensures that when an athlete layer is cleared and repopulated (e.g., via paste),
     *    base layer sections are explicitly hidden rather than showing through.
     */
    clearWorkout(baseLayer) {
        // Remove all sections from the current layer
        const sectionAdds = Layer(this.layer).sectionAdds();
        sectionAdds.forEach((section) => {
            this.removeSection({ workoutSectionId: section.workoutSectionId });
        });
        // If we are clearing an athlete workout with a baseLayer,
        // create removals for any sections in the baseLayer that aren't in the athlete layer
        if (baseLayer) {
            const athleteSections = [
                ...this.layer.sectionAdds,
                ...this.layer.sectionEdits,
            ];
            // Get sections from baseLayer that don't exist in athlete layer
            const baseSections = baseLayer.sectionAdds.filter((bs) => !athleteSections.some((as) => as.workoutSectionId === bs.workoutSectionId));
            // Create removals for base sections not in athlete layer
            baseSections.forEach((section) => {
                this.edits.push({
                    componentName: 'removals',
                    action: 'create',
                    data: {
                        id: v4(),
                        entityType: 'section',
                        entityId: section.workoutSectionId,
                    },
                });
            });
        }
        return this;
    }
    /** Remove a section and all its related content */
    removeSection({ workoutSectionId, }) {
        // Get alternate groups from both layer and pending edits
        const layerAlternateGroupAdds = Layer(this.layer).alternateGroupAdds((e) => e.workoutSectionId === workoutSectionId);
        const pendingAlternateGroupAdds = this.getPendingCreates('alternateGroupAdds', 'workoutSectionId', workoutSectionId);
        const relatedAlternateGroupAdds = [
            ...layerAlternateGroupAdds,
            ...pendingAlternateGroupAdds,
        ];
        // First, collect non-base alternate exercise IDs that will be removed by removeAlternateGroup
        const nonBaseAlternateExerciseIds = new Set();
        relatedAlternateGroupAdds.forEach((alternateGroupAdd) => {
            Layer(this.layer)
                .alternateExerciseAdds((ae) => ae.workoutAlternateGroupId ===
                alternateGroupAdd.workoutAlternateGroupId && ae.sortOrder !== 0)
                .map((ae) => nonBaseAlternateExerciseIds.add(ae.workoutExerciseId));
        });
        // Get exercises from both layer and pending edits
        const layerExerciseAdds = Layer(this.layer).exerciseAdds((e) => e.workoutSectionId === workoutSectionId);
        const pendingExerciseAdds = this.getPendingCreates('exerciseAdds', 'workoutSectionId', workoutSectionId);
        // Remove all exercises in the section, excluding non-base alternates (they'll be removed by removeAlternateGroup)
        [...layerExerciseAdds, ...pendingExerciseAdds].forEach((exerciseAdd) => {
            if (!nonBaseAlternateExerciseIds.has(exerciseAdd.workoutExerciseId)) {
                this.removeExercise({
                    workoutExerciseId: exerciseAdd.workoutExerciseId,
                });
            }
        });
        // Get supersets from both layer and pending edits
        const layerSupersetAdds = Layer(this.layer).supersetAdds((e) => e.workoutSectionId === workoutSectionId);
        const pendingSupersetAdds = this.getPendingCreates('supersetAdds', 'workoutSectionId', workoutSectionId);
        // Remove all supersets in the section
        [...layerSupersetAdds, ...pendingSupersetAdds].forEach((supersetAdd) => {
            this.removeSuperset({ workoutSupersetId: supersetAdd.workoutSupersetId });
        });
        // Remove all alternate groups in the section
        relatedAlternateGroupAdds.forEach((alternateGroupAdd) => {
            this.removeAlternateGroup({
                workoutAlternateGroupId: alternateGroupAdd.workoutAlternateGroupId,
            });
        });
        // Remove the section itself
        const sectionAdd = Layer(this.layer).sectionAdd((s) => s.workoutSectionId === workoutSectionId);
        this.removeEntity({
            componentName: 'sectionAdds',
            entityIdField: 'workoutSectionId',
            entityIdValue: workoutSectionId,
            entityType: 'section',
            layerEntity: sectionAdd,
        });
        return this;
    }
    /** Edit a section */
    editSection({ workoutSectionId, data, }) {
        const sectionAdd = Layer(this.layer).sectionAdd((e) => e.workoutSectionId === workoutSectionId);
        if (sectionAdd) {
            this.edits.push({
                componentName: 'sectionAdds',
                action: 'update',
                data: {
                    ...sectionAdd,
                    ...data,
                },
            });
            return this;
        }
        const sectionEdit = Layer(this.layer).sectionEdit((e) => e.workoutSectionId === workoutSectionId);
        if (sectionEdit) {
            this.edits.push({
                componentName: 'sectionEdits',
                action: 'update',
                data: {
                    ...sectionEdit,
                    ...data,
                },
            });
        }
        else {
            this.edits.push({
                componentName: 'sectionEdits',
                action: 'create',
                data: {
                    ...data,
                    id: v4(),
                    workoutSectionId,
                },
            });
        }
        return this;
    }
    /** Add a new section to the workout */
    addSection({ workoutSectionId, data, }) {
        this.edits.push({
            componentName: 'sectionAdds',
            action: 'create',
            data: { id: v4(), workoutSectionId: workoutSectionId ?? v4(), ...data },
        });
        return this;
    }
    /**
     * Remove an exercise and all its related data (metrics, sets, set metrics)
     *
     * @param workoutExerciseId - The ID of the exercise to remove
     * @param [workoutAlternateGroupId] - (Optional) Only use if all of the alternate exercises and the alternate group itself are in this layer
     * @param [workoutSupersetId] - (Optional) Only use if all of the exercises in the superset and the superset itself are in this layer
     * */
    removeExercise({ workoutExerciseId, workoutAlternateGroupId, workoutSupersetId, }) {
        const exerciseAdd = Layer(this.layer).exerciseAdd((e) => e.workoutExerciseId === workoutExerciseId);
        this.removeEntity({
            componentName: 'exerciseAdds',
            entityIdField: 'workoutExerciseId',
            entityIdValue: workoutExerciseId,
            entityType: 'exercise',
            layerEntity: exerciseAdd,
        });
        // Always remove child entities (metrics and sets)
        this.removeExerciseMetrics({ workoutExerciseId });
        this.removeSets({ workoutExerciseId });
        // this cascade delete only works when all the data for the alternate group is in the same layer
        // see workout-draft.ts, removeExerciseEdits to see the manual logic for the draft layer
        // if exercise is base exercise in workoutAlternateGroupId; if so, remove the alternate group
        if (workoutAlternateGroupId) {
            const alternateExercise = Layer(this.layer).alternateExerciseAdds('workoutExerciseId', workoutExerciseId);
            const targetAlternateExercise = alternateExercise.find((exercise) => exercise.workoutExerciseId === workoutExerciseId);
            if (targetAlternateExercise) {
                this.removeAlternateExercise({
                    workoutAlternateExerciseId: targetAlternateExercise.workoutAlternateExerciseId,
                    workoutAlternateGroupId,
                });
            }
        }
        // this cascade delete only works when all the data for the superset is in the same layer
        // see workout-draft.ts, removeExerciseEdits to see the manual logic for the draft layer
        // if supersetGroup does not have more than 2 exercises, remove the superset
        if (workoutSupersetId) {
            const superset = Layer(this.layer).supersetExerciseAdds({
                workoutSupersetId,
            });
            const exerciseInSuperset = superset.find((ex) => ex.workoutExerciseId === workoutExerciseId);
            if (exerciseInSuperset) {
                this.removeSupersetExercise({
                    workoutSupersetExerciseId: exerciseInSuperset.workoutSupersetExerciseId,
                    workoutSupersetId,
                });
            }
        }
        return this;
    }
    /** Edit an existing exercise's properties */
    editExercise({ workoutExerciseId, data, }) {
        const exerciseAdd = Layer(this.layer).exerciseAdd((e) => e.workoutExerciseId === workoutExerciseId);
        if (exerciseAdd) {
            this.edits.push({
                componentName: 'exerciseAdds',
                action: 'update',
                data: { ...exerciseAdd, ...data },
            });
            return this;
        }
        const exerciseEdit = Layer(this.layer).exerciseEdit((e) => e.workoutExerciseId === workoutExerciseId);
        if (exerciseEdit) {
            this.edits.push({
                componentName: 'exerciseEdits',
                action: 'update',
                data: { ...exerciseEdit, ...data },
            });
            return this;
        }
        else {
            this.edits.push({
                componentName: 'exerciseEdits',
                action: 'create',
                data: { ...data, id: v4(), workoutExerciseId },
            });
            return this;
        }
    }
    /**
     * Add an exercise entry to a section
     *
     * @param [workoutAlternateGroupId] - (Optional) Only use if whole alternate group is within this layer
     * @param [workoutSupersetId] - (Optional) Only use if whole superset is within this layer
     */
    addExercise({ workoutSectionId, workoutExerciseId, data, workoutAlternateGroupId, workoutSupersetId, }) {
        this.edits.push({
            componentName: 'exerciseAdds',
            action: 'create',
            data: {
                ...data,
                id: v4(),
                workoutExerciseId,
                workoutSectionId,
            },
        });
        if (workoutAlternateGroupId) {
            this.addAlternateExercise({
                workoutExerciseId,
                workoutAlternateGroupId,
                data: { sortOrder: data.sortOrder },
            });
        }
        if (workoutSupersetId) {
            this.addSupersetExercise({
                workoutExerciseId,
                workoutSupersetId,
                sortOrder: data.sortOrder,
            });
        }
        return this;
    }
    /** Remove a single set by workoutSetId */
    removeSet({ workoutSetId }) {
        const setAdd = Layer(this.layer).setAdd((e) => e.workoutSetId === workoutSetId);
        this.removeEntity({
            componentName: 'setAdds',
            entityIdField: 'workoutSetId',
            entityIdValue: workoutSetId,
            entityType: 'set',
            layerEntity: setAdd,
        });
        // Always remove setMetrics (handles both layer and pending)
        this.removeSetMetrics({ workoutSetId });
        return this;
    }
    /** Remove all sets for an exercise */
    removeSets({ workoutExerciseId, }) {
        // Get sets from layer
        const setAdds = Layer(this.layer).setAdds((e) => e.workoutExerciseId === workoutExerciseId);
        // Get sets from pending edits
        const pendingSets = this.getPendingCreates('setAdds', 'workoutExerciseId', workoutExerciseId);
        // Remove all sets (both layer and pending)
        [...setAdds, ...pendingSets].forEach((setAdd) => {
            this.removeSet({ workoutSetId: setAdd.workoutSetId });
        });
        return this;
    }
    /** Edit an existing set's properties */
    editSet({ workoutSetId, setMutationData, }) {
        const setAdd = Layer(this.layer).setAdd((e) => e.workoutSetId === workoutSetId);
        if (setAdd) {
            this.edits.push({
                componentName: 'setAdds',
                action: 'update',
                data: { ...setAdd, ...setMutationData },
            });
            return this;
        }
        const setEdit = Layer(this.layer).setEdit((e) => e.workoutSetId === workoutSetId);
        if (setEdit) {
            this.edits.push({
                componentName: 'setEdits',
                action: 'update',
                data: { ...setEdit, ...setMutationData },
            });
            return this;
        }
        else {
            this.edits.push({
                componentName: 'setEdits',
                action: 'create',
                data: { ...setMutationData, id: v4(), workoutSetId },
            });
            return this;
        }
    }
    /**
     * Add sets to an exercise
     *
     * If preserveIds is set, try to use the ids passed in; otherwise generate new ones
     **/
    addSets({ workoutExerciseId, sets, preserveIds = false, }) {
        sets.forEach((setData) => {
            const workoutSetId = preserveIds && setData.workoutSetId ? setData.workoutSetId : v4();
            const workoutSetGroupId = preserveIds && setData.workoutSetGroupId
                ? setData.workoutSetGroupId
                : v4();
            // Add the set
            this.edits.push({
                componentName: 'setAdds',
                action: 'create',
                data: {
                    id: v4(),
                    workoutSetId,
                    workoutExerciseId,
                    sortOrder: setData.sortOrder,
                    workoutSetGroupId,
                },
            });
            // Add set metrics
            if (setData.metrics && setData.metrics.length > 0) {
                this.addSetMetrics({ workoutSetId, metrics: setData.metrics });
            }
        });
        return this;
    }
    /** Remove a single setMetric by workoutSetId and metricId */
    removeSetMetric({ workoutSetId, metricId, }) {
        // Check if setMetric exists in the layer
        const setMetric = Layer(this.layer).setMetrics((e) => e.workoutSetId === workoutSetId && e.metricId === metricId)[0];
        if (setMetric) {
            // Check if we've already created a delete edit for this setMetric (idempotency)
            if (!this.hasPendingDelete('setMetrics', setMetric.id)) {
                // SetMetric is in layer, create delete edit
                this.edits.push({
                    componentName: 'setMetrics',
                    action: 'delete',
                    data: { id: setMetric.id },
                });
            }
        }
        else {
            // Check if it's pending in edits and remove it (don't create delete edit)
            this.removePendingCreateByFields('setMetrics', {
                workoutSetId,
                metricId,
            });
        }
        return this;
    }
    /** Remove all metrics from a set */
    removeSetMetrics({ workoutSetId, }) {
        // Get setMetrics from layer
        const setMetrics = Layer(this.layer).setMetrics((e) => e.workoutSetId === workoutSetId);
        // Get setMetrics from pending edits
        const pendingSetMetrics = this.getPendingCreates('setMetrics', 'workoutSetId', workoutSetId);
        // Remove all setMetrics (both layer and pending)
        [...setMetrics, ...pendingSetMetrics].forEach((setMetric) => {
            this.removeSetMetric({
                workoutSetId: setMetric.workoutSetId,
                metricId: setMetric.metricId,
            });
        });
        return this;
    }
    /** Edit a specific metric within a set */
    editSetMetric({ workoutSetId, metricId, data, }) {
        // Prefer the entry already carrying the incoming source so re-editing a
        // Manual Override updates it in place instead of stacking duplicates.
        const setMetric = (data.dataSource != null &&
            Layer(this.layer).setMetric((e) => e.workoutSetId === workoutSetId &&
                e.metricId === metricId &&
                e.dataSource === data.dataSource)) ||
            Layer(this.layer).setMetric((e) => e.workoutSetId === workoutSetId && e.metricId === metricId);
        if (setMetric &&
            (data.dataSource == null || setMetric.dataSource === data.dataSource)) {
            this.edits.push({
                componentName: 'setMetrics',
                action: 'update',
                data: {
                    ...setMetric,
                    ...data,
                    workoutSetId,
                },
            });
        }
        else if (setMetric) {
            // Cross-source override (e.g. a practitioner entry over a Catapult
            // value): append a source-distinct entry rather than rewrite the feed
            // entry — the merge retains the displaced source for the hover, and
            // rewriting in place would destroy the feed value.
            this.edits.push({
                componentName: 'setMetrics',
                action: 'create',
                data: {
                    id: v4(),
                    workoutSetId,
                    metricId,
                    input: data.input,
                    forCollection: data.forCollection,
                    dataSource: data.dataSource,
                },
            });
        }
        else {
            this.addSetMetrics({ workoutSetId, metrics: [data] });
        }
        return this;
    }
    /**
     * Add metrics to a set
     * Filters out duplicate metricIds from:
     * 1. Within the incoming metrics array (keeps first occurrence)
     * 2. Metrics that already exist in the layer for this set
     * 3. Metrics that are pending in edits but not yet merged
     */
    addSetMetrics({ workoutSetId, metrics, }) {
        // Get existing metrics for this set from the layer
        const existingMetrics = Layer(this.layer).setMetrics((e) => e.workoutSetId === workoutSetId);
        const existingMetricIds = new Set(existingMetrics.map((m) => m.metricId));
        // Get pending metrics being added for this set
        const pendingMetrics = this.getPendingCreates('setMetrics', 'workoutSetId', workoutSetId);
        const pendingMetricIds = new Set(pendingMetrics.map((m) => m.metricId));
        // Filter out duplicates from the incoming array, the layer, and pending edits
        const uniqueMetrics = metrics.filter((metric, index, self) => {
            const isFirstOccurrence = self.findIndex((m) => m.metricId === metric.metricId) === index;
            const notAlreadyExists = !existingMetricIds.has(metric.metricId);
            const notPending = !pendingMetricIds.has(metric.metricId);
            return isFirstOccurrence && notAlreadyExists && notPending;
        });
        uniqueMetrics.forEach((metric) => {
            this.edits.push({
                componentName: 'setMetrics',
                action: 'create',
                data: {
                    id: v4(),
                    workoutSetId,
                    metricId: metric.metricId,
                    input: metric.input,
                    forCollection: metric.forCollection,
                    dataSource: metric.dataSource,
                },
            });
        });
        return this;
    }
    /**
     * Remove a single exercise metric by workoutExerciseMetricId
     * Also removes all corresponding setMetrics for this metric from all sets in the exercise
     */
    removeExerciseMetric({ workoutExerciseMetricId, }) {
        // First, get the metric data (from layer or pending) before removing
        // Note: A metric can only be in layer OR pending, never both
        const exerciseMetricAdd = Layer(this.layer).exerciseMetricAdd((m) => m.workoutExerciseMetricId === workoutExerciseMetricId);
        const pendingMetric = !exerciseMetricAdd
            ? this.getPendingCreates('exerciseMetricAdds', 'workoutExerciseMetricId', workoutExerciseMetricId)[0]
            : undefined;
        const metricData = exerciseMetricAdd || pendingMetric;
        // Remove the exercise metric using removeEntity helper
        this.removeEntity({
            componentName: 'exerciseMetricAdds',
            entityIdField: 'workoutExerciseMetricId',
            entityIdValue: workoutExerciseMetricId,
            entityType: 'exerciseMetric',
            layerEntity: exerciseMetricAdd,
        });
        // If we have metric data, cascade delete setMetrics
        if (metricData) {
            const { metricId, workoutExerciseId } = metricData;
            // Remove all setMetrics with this metricId from all sets in this exercise
            const existingSets = Layer(this.layer).setAdds({ workoutExerciseId });
            const pendingSets = this.getPendingCreates('setAdds', 'workoutExerciseId', workoutExerciseId);
            const allSets = [...existingSets, ...pendingSets];
            // removeSetMetric handles both layer and pending setMetrics automatically
            allSets.forEach((set) => {
                this.removeSetMetric({
                    workoutSetId: set.workoutSetId,
                    metricId,
                });
            });
        }
        return this;
    }
    /** Remove all metrics from an exercise */
    removeExerciseMetrics({ workoutExerciseId, otherExerciseMetrics, }) {
        // Get metrics from layer
        const exerciseMetricAdds = Layer(this.layer).exerciseMetricAdds((e) => e.workoutExerciseId === workoutExerciseId);
        // Get metrics from pending edits
        const pendingMetrics = this.getPendingCreates('exerciseMetricAdds', 'workoutExerciseId', workoutExerciseId);
        // Remove all metrics (both layer and pending)
        [
            ...exerciseMetricAdds,
            ...(otherExerciseMetrics ?? []),
            ...pendingMetrics,
        ].forEach((metric) => {
            this.removeExerciseMetric({
                workoutExerciseMetricId: metric.workoutExerciseMetricId,
            });
        });
        return this;
    }
    /** Edit an existing exercise metric's properties */
    editExerciseMetric({ workoutExerciseMetricId, data, }) {
        const exerciseMetricAdd = Layer(this.layer).exerciseMetricAdd((e) => e.workoutExerciseMetricId === workoutExerciseMetricId);
        if (exerciseMetricAdd) {
            this.edits.push({
                componentName: 'exerciseMetricAdds',
                action: 'update',
                data: { ...exerciseMetricAdd, ...data },
            });
        }
        return this;
    }
    /**
     * Add metrics to an exercise
     * Filters out duplicate metricIds from:
     * 1. Within the incoming metrics array (keeps first occurrence)
     * 2. Metrics that already exist in the layer for this exercise
     * 3. Metrics that are pending creates but not yet merged
     *
     * Also creates setMetrics for all existing sets with the new metrics
     * If preserveIds is set, try to use the ids passed in; otherwise generate new ones
     */
    addExerciseMetrics({ workoutExerciseId, metrics, otherSets, otherMetrics, preserveIds = false, }) {
        const layer = Layer(this.layer);
        // Get existing metrics for this exercise from the layer
        const existingMetrics = layer.exerciseMetricAdds({ workoutExerciseId });
        const existingMetricIds = new Set([...existingMetrics, ...(otherMetrics ?? [])].map((m) => m.metricId));
        // Get pending metrics being added for this exercise
        const pendingMetrics = this.getPendingCreates('exerciseMetricAdds', 'workoutExerciseId', workoutExerciseId);
        const pendingMetricIds = new Set(pendingMetrics.map((m) => m.metricId));
        // Filter out duplicates from the incoming array, the layer, and pending edits
        const uniqueMetrics = metrics.filter((metric, index, self) => {
            const isFirstOccurrence = self.findIndex((m) => m.metricId === metric.metricId) === index;
            const notAlreadyExists = !existingMetricIds.has(metric.metricId);
            const notPending = !pendingMetricIds.has(metric.metricId);
            return isFirstOccurrence && notAlreadyExists && notPending;
        });
        // Add exercise metric edits
        uniqueMetrics.forEach((metric) => {
            this.edits.push({
                componentName: 'exerciseMetricAdds',
                action: 'create',
                data: {
                    id: v4(),
                    workoutExerciseId,
                    workoutExerciseMetricId: preserveIds && metric.workoutExerciseMetricId
                        ? metric.workoutExerciseMetricId
                        : v4(),
                    metricId: metric.metricId,
                    metricName: metric.metricName,
                    metricType: metric.metricType,
                    sortOrder: metric.sortOrder,
                    ...(metric.benchmarkGridId !== undefined && {
                        benchmarkGridId: metric.benchmarkGridId,
                    }),
                },
            });
        });
        // Get all existing sets for this exercise from the layer
        const existingSets = layer.setAdds({ workoutExerciseId });
        // Also check pending edits for sets being added
        const pendingSets = this.getPendingCreates('setAdds', 'workoutExerciseId', workoutExerciseId);
        const totalSets = [...existingSets, ...pendingSets, ...(otherSets ?? [])];
        // For each new metric, create setMetrics for all existing sets
        // This ensures data integrity - every set should have a value for every metric
        if (totalSets.length > 0 && uniqueMetrics.length > 0) {
            for (const set of totalSets) {
                // Use this.addSetMetrics to handle duplicate checking and creation
                this.addSetMetrics({
                    workoutSetId: set.workoutSetId,
                    metrics: uniqueMetrics.map((metric) => ({
                        metricId: metric.metricId,
                        // Sets metric should default to '1', other metrics default to empty
                        input: metric.metricType === 'sets' ? '1' : '',
                        forCollection: false,
                    })),
                });
            }
        }
        return this;
    }
    /** Remove all alternate exercises for a given alternate group or exercise */
    removeAlternateExercises({ workoutAlternateGroupId, workoutExerciseId, }) {
        // Define filter function once for both layer and pending queries
        const alternateExerciseFilter = (e) => {
            if (workoutAlternateGroupId && workoutExerciseId) {
                return (e.workoutAlternateGroupId === workoutAlternateGroupId &&
                    e.workoutExerciseId === workoutExerciseId);
            }
            if (workoutAlternateGroupId) {
                return e.workoutAlternateGroupId === workoutAlternateGroupId;
            }
            if (workoutExerciseId) {
                return e.workoutExerciseId === workoutExerciseId;
            }
            return false;
        };
        // Get alternate exercises from layer
        const altExerciseAdds = Layer(this.layer).alternateExerciseAdds(alternateExerciseFilter);
        // Get alternate exercises from pending edits
        const pendingAltExercises = this.getPendingCreates('alternateExerciseAdds').filter(alternateExerciseFilter);
        // Remove from layer (create delete edits)
        altExerciseAdds.forEach((altExerciseAdd) => {
            if (!this.hasPendingDelete('alternateExerciseAdds', altExerciseAdd.id)) {
                this.edits.push({
                    componentName: 'alternateExerciseAdds',
                    action: 'delete',
                    data: { id: altExerciseAdd.id },
                });
            }
        });
        // Remove from pending (splice out the create edits)
        pendingAltExercises.forEach((pendingAlt) => {
            this.removePendingCreate('alternateExerciseAdds', 'workoutAlternateExerciseId', pendingAlt.workoutAlternateExerciseId);
        });
        return this;
    }
    /** Remove an alternate group */
    removeAlternateGroup({ workoutAlternateGroupId, }) {
        const groupAdds = Layer(this.layer).alternateGroupAdds((g) => g.workoutAlternateGroupId === workoutAlternateGroupId);
        this.removeEntity({
            componentName: 'alternateGroupAdds',
            entityIdField: 'workoutAlternateGroupId',
            entityIdValue: workoutAlternateGroupId,
            entityType: 'alternateGroup',
            layerEntity: groupAdds[0],
        });
        // A group id resolves to one row, but layers written before that was enforced can
        // hold several. Delete the extras as well, otherwise a leftover row keeps the group
        // in the merged workout and removing it looks like it did nothing.
        groupAdds.slice(1).forEach((duplicateGroupAdd) => {
            if (!this.hasPendingDelete('alternateGroupAdds', duplicateGroupAdd.id)) {
                this.edits.push({
                    componentName: 'alternateGroupAdds',
                    action: 'delete',
                    data: { id: duplicateGroupAdd.id },
                });
            }
        });
        // Remove all alternate exercises (link removal only)
        this.removeAlternateExercises({ workoutAlternateGroupId });
        // Get non-base alternate exercises from both layer and pending edits
        const layerAlternateExercises = Layer(this.layer)
            .alternateExerciseAdds((ae) => ae.workoutAlternateGroupId === workoutAlternateGroupId &&
            ae.sortOrder !== 0)
            .map((ae) => ae.workoutExerciseId);
        const pendingAlternateExercises = this.getPendingCreates('alternateExerciseAdds', 'workoutAlternateGroupId', workoutAlternateGroupId)
            .filter((ae) => ae.sortOrder !== 0)
            .map((ae) => ae.workoutExerciseId);
        // Remove all non-base alternate exercises
        [...layerAlternateExercises, ...pendingAlternateExercises].forEach((workoutExerciseId) => {
            this.removeExercise({ workoutExerciseId });
        });
        return this;
    }
    /** Edit an alternate group */
    editAlternateGroup({ workoutAlternateGroupId, data, }) {
        const groupAdd = Layer(this.layer).alternateGroupAdd((g) => g.workoutAlternateGroupId === workoutAlternateGroupId);
        if (groupAdd) {
            this.edits.push({
                componentName: 'alternateGroupAdds',
                action: 'update',
                data: {
                    ...groupAdd,
                    ...data,
                },
            });
            return this;
        }
        const alternateGroupEdit = Layer(this.layer).alternateGroupEdit((g) => g.workoutAlternateGroupId === workoutAlternateGroupId);
        if (alternateGroupEdit) {
            this.edits.push({
                componentName: 'alternateGroupEdits',
                action: 'update',
                data: {
                    ...alternateGroupEdit,
                    ...data,
                },
            });
        }
        else {
            this.edits.push({
                componentName: 'alternateGroupEdits',
                action: 'create',
                data: {
                    id: v4(),
                    workoutAlternateGroupId,
                    ...data,
                },
            });
        }
        return this;
    }
    /** Add an alternate group with its first exercise */
    addAlternateGroup({ workoutSectionId, workoutExerciseId, workoutAlternateGroupId, sortOrder, }) {
        const groupId = workoutAlternateGroupId ?? v4();
        // Create the alternate group
        this.edits.push({
            action: 'create',
            componentName: 'alternateGroupAdds',
            data: {
                id: v4(),
                sortOrder,
                workoutAlternateGroupId: groupId,
                workoutSectionId,
                selectedExerciseId: workoutExerciseId,
            },
        });
        // Add the first exercise to the group
        this.edits.push({
            action: 'create',
            componentName: 'alternateExerciseAdds',
            data: {
                id: v4(),
                sortOrder: 0,
                workoutAlternateExerciseId: v4(),
                workoutAlternateGroupId: groupId,
                workoutExerciseId,
            },
        });
        return this;
    }
    /**
     * Removes an alternate exercise from the layer, but also removes the alternate group if the exercise is the base exercise
     *
     * @param workoutAlternateExerciseId - The ID of the alternate exercise to remove
     * @param [workoutAlternateGroupId] - (Optional) Only use if the alternate exercise spans across multiple layers
     * @param [otherAlternateExercises] - (Optional) Use to give context to alternate exercise is another layer
     * */
    removeAlternateExercise({ workoutAlternateExerciseId, workoutAlternateGroupId, otherAlternateExercises = [], }) {
        const altExerciseAdd = Layer(this.layer).alternateExerciseAdd({
            workoutAlternateExerciseId,
        });
        const targetAlternateExercise = [
            ...otherAlternateExercises,
            altExerciseAdd,
        ].find((exercise) => exercise?.workoutAlternateExerciseId === workoutAlternateExerciseId);
        // If removing base exercise (sortOrder === 0), remove entire group
        if (workoutAlternateGroupId && targetAlternateExercise?.sortOrder === 0) {
            this.removeAlternateGroup({ workoutAlternateGroupId });
            // Remove base layer alternate exercises
            otherAlternateExercises.forEach((exercise) => {
                if (exercise.sortOrder !== 0) {
                    this.removeExercise({
                        workoutExerciseId: exercise.workoutExerciseId,
                    });
                    this.removeAlternateExercise({
                        workoutAlternateExerciseId: exercise.workoutAlternateExerciseId,
                    });
                }
            });
        }
        else {
            // Just remove the alternate exercise
            this.removeEntity({
                componentName: 'alternateExerciseAdds',
                entityIdField: 'workoutAlternateExerciseId',
                entityIdValue: workoutAlternateExerciseId,
                entityType: 'alternateExercise',
                layerEntity: altExerciseAdd,
            });
        }
        return this;
    }
    /** Edit an alternate exercise */
    editAlternateExercise({ workoutAlternateExerciseId, data, }) {
        const alternateExerciseAdd = Layer(this.layer).alternateExerciseAdd((e) => e.workoutAlternateExerciseId === workoutAlternateExerciseId);
        if (alternateExerciseAdd) {
            this.edits.push({
                componentName: 'alternateExerciseAdds',
                action: 'update',
                data: { ...alternateExerciseAdd, ...data },
            });
            return this;
        }
        const alternateExerciseEdit = Layer(this.layer).alternateExerciseEdit((e) => e.workoutAlternateExerciseId === workoutAlternateExerciseId);
        if (alternateExerciseEdit) {
            this.edits.push({
                componentName: 'alternateExerciseEdits',
                action: 'update',
                data: { ...alternateExerciseEdit, ...data },
            });
            return this;
        }
        else {
            this.edits.push({
                componentName: 'alternateExerciseEdits',
                action: 'create',
                data: { ...data, id: v4(), workoutAlternateExerciseId },
            });
            return this;
        }
    }
    /** Add an exercise to an existing alternate group */
    addAlternateExercise({ workoutExerciseId, workoutAlternateGroupId, data, }) {
        this.edits.push({
            componentName: 'alternateExerciseAdds',
            action: 'create',
            data: {
                id: v4(),
                workoutAlternateExerciseId: v4(),
                workoutAlternateGroupId,
                workoutExerciseId: workoutExerciseId ?? v4(),
                sortOrder: this.resolveAlternateExerciseSortOrder(workoutAlternateGroupId, data.sortOrder),
            },
        });
        return this;
    }
    /** Remove all superset exercises for a given superset or exercise */
    removeSupersetExercises({ workoutSupersetId, workoutExerciseId, }) {
        // Define filter function once for both layer and pending queries
        const supersetExerciseFilter = (m) => {
            if (workoutSupersetId && workoutExerciseId) {
                return (m.workoutSupersetId === workoutSupersetId &&
                    m.workoutExerciseId === workoutExerciseId);
            }
            if (workoutSupersetId) {
                return m.workoutSupersetId === workoutSupersetId;
            }
            if (workoutExerciseId) {
                return m.workoutExerciseId === workoutExerciseId;
            }
            return false;
        };
        // Get superset exercises from layer
        const supersetExerciseAdds = Layer(this.layer).supersetExerciseAdds(supersetExerciseFilter);
        // Get superset exercises from pending edits
        const pendingSupersetExercises = this.getPendingCreates('supersetExerciseAdds').filter(supersetExerciseFilter);
        // Remove from layer (create delete edits)
        supersetExerciseAdds.forEach((supersetExerciseAdd) => {
            if (!this.hasPendingDelete('supersetExerciseAdds', supersetExerciseAdd.id)) {
                this.edits.push({
                    componentName: 'supersetExerciseAdds',
                    action: 'delete',
                    data: { id: supersetExerciseAdd.id },
                });
            }
        });
        // Remove from pending (splice out the create edits)
        pendingSupersetExercises.forEach((pending) => {
            this.removePendingCreate('supersetExerciseAdds', 'workoutSupersetExerciseId', pending.workoutSupersetExerciseId);
        });
        return this;
    }
    /** Remove a superset */
    removeSuperset({ workoutSupersetId, exerciseSortOrders, }) {
        // Update exercise sort orders if provided
        if (exerciseSortOrders?.length) {
            exerciseSortOrders.forEach(({ workoutExerciseId, sortOrder }) => {
                this.editExercise({ workoutExerciseId, data: { sortOrder } });
            });
        }
        // Find and delete/remove the superset
        const supersetAdd = Layer(this.layer).supersetAdd((e) => e.workoutSupersetId === workoutSupersetId);
        this.removeEntity({
            componentName: 'supersetAdds',
            entityIdField: 'workoutSupersetId',
            entityIdValue: workoutSupersetId,
            entityType: 'superset',
            layerEntity: supersetAdd,
        });
        // Remove all superset exercises
        this.removeSupersetExercises({ workoutSupersetId });
        return this;
    }
    /** Edit a superset */
    editSuperset({ workoutSupersetId, data, }) {
        const supersetAdd = Layer(this.layer).supersetAdd((e) => e.workoutSupersetId === workoutSupersetId);
        if (supersetAdd) {
            this.edits.push({
                componentName: 'supersetAdds',
                action: 'update',
                data: {
                    ...supersetAdd,
                    ...data,
                },
            });
            return this;
        }
        const supersetEdit = Layer(this.layer).supersetEdit((e) => e.workoutSupersetId === workoutSupersetId);
        if (supersetEdit) {
            this.edits.push({
                componentName: 'supersetEdits',
                action: 'update',
                data: {
                    ...supersetEdit,
                    ...data,
                },
            });
            return this;
        }
        else {
            this.edits.push({
                componentName: 'supersetEdits',
                action: 'create',
                data: {
                    ...data,
                    id: v4(),
                    workoutSupersetId,
                },
            });
            return this;
        }
    }
    /** Add a new superset to a section, optionally with initial exercises */
    addSuperset({ workoutSectionId, sortOrder, workoutSupersetId: _workoutSupersetId, supersetExercises, }) {
        const workoutSupersetId = _workoutSupersetId ?? v4();
        this.edits.push({
            componentName: 'supersetAdds',
            action: 'create',
            data: {
                id: v4(),
                workoutSectionId,
                workoutSupersetId: workoutSupersetId,
                sortOrder,
            },
        });
        for (const { workoutExerciseId, sortOrder } of supersetExercises ?? []) {
            this.addSupersetExercise({
                workoutExerciseId,
                workoutSupersetId,
                sortOrder,
            });
        }
        return this;
    }
    /** Merge two supersets by moving exercises from one to another and removing the first */
    mergeSuperset({ supersetIdToRemove, supersetIdToJoin, updatedSupersetSortOrder, exerciseSortOrders, }) {
        this.removeSuperset({
            workoutSupersetId: supersetIdToRemove,
            exerciseSortOrders,
        });
        this.editSuperset({
            workoutSupersetId: supersetIdToJoin,
            data: { sortOrder: updatedSupersetSortOrder },
        });
        exerciseSortOrders.forEach(({ workoutExerciseId, sortOrder }) => {
            this.addSupersetExercise({
                workoutExerciseId,
                workoutSupersetId: supersetIdToJoin,
                sortOrder,
            });
        });
        return this;
    }
    /** Add an exercise to a superset */
    addSupersetExercise({ workoutExerciseId, workoutSupersetId, sortOrder, }) {
        const workoutSupersetExerciseId = v4();
        this.edits.push({
            componentName: 'supersetExerciseAdds',
            action: 'create',
            data: {
                id: v4(),
                workoutExerciseId,
                sortOrder,
                workoutSupersetExerciseId,
                workoutSupersetId,
            },
        });
        return this;
    }
    /** Edit a superset exercise */
    editSupersetExercise({ workoutSupersetExerciseId, data, }) {
        const supersetExerciseAdd = Layer(this.layer).supersetExerciseAdd((e) => e.workoutSupersetExerciseId === workoutSupersetExerciseId);
        if (supersetExerciseAdd) {
            this.edits.push({
                componentName: 'supersetExerciseAdds',
                action: 'update',
                data: {
                    ...supersetExerciseAdd,
                    ...data,
                },
            });
            return this;
        }
        const exerciseEdit = Layer(this.layer).supersetExerciseEdit((e) => e.workoutSupersetExerciseId === workoutSupersetExerciseId);
        if (exerciseEdit) {
            this.edits.push({
                componentName: 'supersetExerciseEdits',
                action: 'update',
                data: {
                    ...exerciseEdit,
                    ...data,
                },
            });
        }
        else {
            this.edits.push({
                componentName: 'supersetExerciseEdits',
                action: 'create',
                data: {
                    ...data,
                    id: v4(),
                    workoutSupersetExerciseId,
                },
            });
        }
        return this;
    }
    /**
     * Remove an exercise from a superset and also removes the superset if it has less than 2 exercises
     *
     * @param workoutSupersetExerciseId - The ID of the superset exercise to remove
     * @param [workoutSupersetId] - (Optional) Only use if the superset spans across multiple layers
     * @param [otherSupersetExercises] - (Optional) Use to give context to superset exercise is another layer
     * */
    removeSupersetExercise({ workoutSupersetExerciseId, workoutSupersetId, otherSupersetExercises = [], }) {
        // If workoutSupersetId provided, check if we should remove entire superset FIRST
        // This prevents double-deletion errors
        if (workoutSupersetId) {
            const layerSupersetExercises = Layer(this.layer).supersetExerciseAdds('workoutSupersetId', workoutSupersetId);
            // Get pending superset exercises as well
            const pendingSupersetExercises = this.getPendingCreates('supersetExerciseAdds', 'workoutSupersetId', workoutSupersetId);
            // Merge with base layer exercises if provided
            const totalExercises = [
                ...layerSupersetExercises,
                ...pendingSupersetExercises,
                ...otherSupersetExercises,
            ];
            // If <= 2 exercises total, remove entire superset and return early
            if (totalExercises.length <= 2) {
                this.removeSuperset({ workoutSupersetId });
                return this;
            }
        }
        // Only remove individual exercise if we're not removing the whole superset
        const supersetExerciseAdd = Layer(this.layer).supersetExerciseAdd((e) => e.workoutSupersetExerciseId === workoutSupersetExerciseId);
        this.removeEntity({
            componentName: 'supersetExerciseAdds',
            entityIdField: 'workoutSupersetExerciseId',
            entityIdValue: workoutSupersetExerciseId,
            entityType: 'supersetExercise',
            layerEntity: supersetExerciseAdd,
        });
        return this;
    }
    /**
     * Swap an exercise with a new one, removing incompatible metrics and preserving sets and metrics where applicable
     * When replace is true, we remove the old exercise and add a new one with a new ID
     * When replace is false, we edit the exercise keeping the existing workoutExerciseId
     *
     * If preserveIds is set, try to use the ids passed in; otherwise generate new ones
     */
    swapExercise({ replaceExercise, workoutSectionId, previousWorkoutExerciseId, exerciseData: newExerciseData, metrics: newMetrics, sets: newSets = [], // workoutSupersetId,
    superset, alternateGroup, // supersetSortOrder,
    preserveIds = false, }) {
        if (replaceExercise) {
            // ATHLETE LAYER: Remove old exercise and add new one
            // Need to pass in superset and alternate group data since we might not have access to that info on the athlete layer
            const newWorkoutExerciseId = preserveIds && newExerciseData.workoutExerciseId
                ? newExerciseData.workoutExerciseId
                : v4();
            // Remove the old exercise (this also removes its metrics, sets, and references)
            this.removeExercise({ workoutExerciseId: previousWorkoutExerciseId });
            // Remove set metrics that are incompatible with the new exercise
            // (only keep set metrics whose metricId exists in the new exercise's metrics)
            const filteredSets = newSets.map((set) => ({
                ...set,
                metrics: set.metrics.filter((m) => newMetrics.some((nm) => nm.metricId === m.metricId)),
            }));
            // Add the new exercise with filtered metrics and sets
            this.addExercise({
                workoutSectionId,
                workoutExerciseId: newWorkoutExerciseId,
                data: newExerciseData,
            })
                .addExerciseMetrics({
                workoutExerciseId: newWorkoutExerciseId,
                metrics: newMetrics,
                preserveIds,
            })
                .addSets({
                workoutExerciseId: newWorkoutExerciseId,
                sets: filteredSets,
                preserveIds,
            });
            if (alternateGroup && alternateGroup.workoutAlternateGroupId) {
                const { workoutAlternateGroupId, sortOrder } = alternateGroup;
                // Drop the outgoing exercise's link first: when it is the group's base, the
                // incoming one takes its sortOrder 0 slot
                this.removeAlternateExercises({
                    workoutExerciseId: previousWorkoutExerciseId,
                });
                this.addAlternateExercise({
                    workoutExerciseId: newWorkoutExerciseId,
                    workoutAlternateGroupId: workoutAlternateGroupId,
                    data: { sortOrder: sortOrder },
                });
            }
            if (superset && superset.workoutSupersetId) {
                const { workoutSupersetId, sortOrder } = superset;
                // Same ordering as the alternate group above: drop the outgoing exercise's link
                // first, so a swap that reuses the exercise id keeps the incoming link
                this.removeSupersetExercises({
                    workoutExerciseId: previousWorkoutExerciseId,
                });
                this.addSupersetExercise({
                    workoutExerciseId: newWorkoutExerciseId,
                    workoutSupersetId: workoutSupersetId,
                    sortOrder: sortOrder,
                });
            }
        }
        else {
            // BASE LAYER: Edit exercise in place to keep the same workoutExerciseId
            // Find existing metrics and sets for the old exercise
            const existingExerciseMetrics = Layer(this.layer).exerciseMetricAdds((m) => m.workoutExerciseId === previousWorkoutExerciseId);
            const existingSets = Layer(this.layer).setAdds((s) => s.workoutExerciseId === previousWorkoutExerciseId);
            const existingSetIds = existingSets.map((s) => s.workoutSetId);
            const existingSetMetrics = Layer(this.layer).setMetrics((sm) => existingSetIds.includes(sm.workoutSetId));
            // Find metrics that need to be removed (exist in old but not in new)
            const metricsToRemove = existingExerciseMetrics.filter((m) => !newMetrics.some((nm) => nm.metricId === m.metricId));
            // Find set metrics that need to be removed (metric no longer exists)
            const setMetricsToRemove = existingSetMetrics.filter((sm) => !newMetrics.some((nm) => nm.metricId === sm.metricId));
            // Remove old metrics
            metricsToRemove.forEach((m) => {
                this.removeExerciseMetric({
                    workoutExerciseMetricId: m.workoutExerciseMetricId,
                });
            });
            // Remove old set metrics
            setMetricsToRemove.forEach((sm) => {
                this.removeSetMetrics({ workoutSetId: sm.workoutSetId });
            });
            // Update the exercise in place (keeps same workoutExerciseId)
            this.editExercise({
                workoutExerciseId: previousWorkoutExerciseId,
                data: newExerciseData,
            });
        }
        return this;
    }
    /**
     * Collect and organize entry data (exercises, alternate groups, supersets)
     * Supports multiple entries of each type
     *
     * Returns:
     * - allUniqueExercises: All unique exercises across all entries (for creating exercises with new IDs)
     * - allAlternateGroups: ALL alternate groups including those in supersets (for removal operations)
     * - allSupersets: All superset structures found in entries
     * - allStandaloneExercises: Exercises not belonging to any structure (alternate group or superset)
     * - onlyAlternateGroups: Alternate groups that are NOT part of supersets (processed directly)
     * - supersetAlternateGroupMap: Maps superset IDs to their contained alternate group IDs
     */
    collectEntryData(entries) {
        const allUniqueExercises = new Map();
        const allAlternateGroups = [];
        const allSupersets = [];
        const allStandaloneExercises = [];
        const supersetAlternateGroupMap = new Map();
        // Track which exercises and alternate groups belong to structures
        const exercisesInStructures = new Set();
        const alternateGroupsInSupersets = new Set();
        // Collect all supersets and track relationships
        entries.forEach((entry) => {
            if (entry.__type === 'WorkoutSuperset') {
                allSupersets.push(entry);
                // Collect exercises from this superset
                entry.exercises.forEach(({ exercise }) => {
                    if (exercise.workoutExerciseId) {
                        allUniqueExercises.set(exercise.workoutExerciseId, exercise);
                        exercisesInStructures.add(exercise.workoutExerciseId);
                    }
                });
            }
        });
        // Collect all alternate groups and determine which belong to supersets
        entries.forEach((entry) => {
            if (entry.__type === 'WorkoutAlternateGroup') {
                allAlternateGroups.push(entry);
                const altGroupId = entry.workoutAlternateGroupId ?? '';
                // Find the primary exercise (sortOrder 0) of this alternate group
                const primaryExercise = entry.exercises.find(({ sortOrder }) => sortOrder === 0)?.exercise;
                // Collect all exercises from this alternate group
                entry.exercises.forEach(({ exercise }) => {
                    if (exercise.workoutExerciseId) {
                        allUniqueExercises.set(exercise.workoutExerciseId, exercise);
                        exercisesInStructures.add(exercise.workoutExerciseId);
                    }
                });
                // Check if the primary exercise belongs to a superset
                if (primaryExercise?.workoutExerciseId) {
                    allSupersets.forEach((superset) => {
                        const belongsToSuperset = superset.exercises.some(({ exercise: supersetEx }) => supersetEx.workoutExerciseId ===
                            primaryExercise.workoutExerciseId);
                        if (belongsToSuperset) {
                            const supersetId = superset.workoutSupersetId ?? '';
                            const existingAltGroups = supersetAlternateGroupMap.get(supersetId) || [];
                            if (!existingAltGroups.includes(altGroupId)) {
                                supersetAlternateGroupMap.set(supersetId, [
                                    ...existingAltGroups,
                                    altGroupId,
                                ]);
                                alternateGroupsInSupersets.add(altGroupId);
                            }
                        }
                    });
                }
            }
        });
        // Filter out alternate groups that belong to supersets
        const onlyAlternateGroups = allAlternateGroups.filter((ag) => !alternateGroupsInSupersets.has(ag.workoutAlternateGroupId ?? ''));
        // Collect standalone exercises (not in any structure)
        entries.forEach((entry) => {
            if (entry.__type === 'WorkoutExercise') {
                if (!exercisesInStructures.has(entry.workoutExerciseId ?? '')) {
                    allStandaloneExercises.push(entry);
                    if (entry.workoutExerciseId) {
                        allUniqueExercises.set(entry.workoutExerciseId, entry);
                    }
                }
            }
        });
        return {
            allUniqueExercises,
            allSupersets,
            allAlternateGroups,
            allStandaloneExercises,
            onlyAlternateGroups,
            supersetAlternateGroupMap,
        };
    }
    /** Remove entries (exercises, alternate groups, supersets) from their current location */
    removeEntries({ allUniqueExercises, onlyAlternateGroups, allSupersets, supersetAlternateGroupMap, allAlternateGroups, }) {
        // Collect all non-base exercise IDs from all alternate groups
        // These will be removed by removeAlternateGroup, so we shouldn't try to remove them again
        const nonBaseAlternateExerciseIds = new Set();
        allAlternateGroups.forEach((alternateGroup) => {
            alternateGroup.exercises.forEach(({ exercise, sortOrder }) => {
                // sortOrder !== 0 means it's an alternate exercise, not the base
                if (sortOrder !== 0 && exercise.workoutExerciseId) {
                    nonBaseAlternateExerciseIds.add(exercise.workoutExerciseId);
                }
            });
        });
        // Remove all standalone alternate groups
        onlyAlternateGroups.forEach((alternateGroup) => {
            this.removeAlternateGroup({
                workoutAlternateGroupId: alternateGroup.workoutAlternateGroupId ?? '',
            });
        });
        // Remove alternate groups that belong to supersets
        supersetAlternateGroupMap.forEach((altGroupIds) => {
            altGroupIds.forEach((altGroupId) => {
                this.removeAlternateGroup({
                    workoutAlternateGroupId: altGroupId,
                });
            });
        });
        // Remove all supersets
        allSupersets.forEach((superset) => {
            this.removeSuperset({
                workoutSupersetId: superset.workoutSupersetId ?? '',
            });
        });
        // Remove all unique exercises, excluding those already removed by removeAlternateGroup
        allUniqueExercises.forEach((exercise) => {
            const exerciseId = exercise.workoutExerciseId ?? '';
            // Skip exercises that were already removed as non-base alternates
            if (!nonBaseAlternateExerciseIds.has(exerciseId)) {
                this.removeExercise({
                    workoutExerciseId: exerciseId,
                });
            }
        });
        return this;
    }
    /**
     * Add entries to a section with new IDs
     * Supports multiple supersets, alternate groups, and standalone exercises
     */
    addEntriesToSection({ targetSectionId, targetSortOrder, allUniqueExercises, onlyAlternateGroups, allSupersets, supersetAlternateGroupMap, allAlternateGroups, }) {
        // Map old exercise IDs to new IDs to maintain relationships
        const exerciseIdMap = new Map();
        // Create all exercises with new IDs
        allUniqueExercises.forEach((exercise) => {
            const newWorkoutExerciseId = v4();
            const oldWorkoutExerciseId = exercise.workoutExerciseId;
            if (oldWorkoutExerciseId) {
                exerciseIdMap.set(oldWorkoutExerciseId, newWorkoutExerciseId);
            }
            // Use the exercise's original sort order
            const sortOrder = targetSortOrder + exercise.sortOrder;
            this.addExercise({
                workoutSectionId: targetSectionId,
                workoutExerciseId: newWorkoutExerciseId,
                data: { ...exercise, sortOrder },
            })
                .addExerciseMetrics({
                workoutExerciseId: newWorkoutExerciseId,
                metrics: exercise.metrics,
            })
                .addSets({
                workoutExerciseId: newWorkoutExerciseId,
                sets: exercise.sets,
            });
        });
        // Recreate all superset structures
        allSupersets.forEach((superset) => {
            const newSupersetId = v4();
            const oldSupersetId = superset.workoutSupersetId ?? '';
            this.addSuperset({
                workoutSectionId: targetSectionId,
                workoutSupersetId: newSupersetId,
                sortOrder: targetSortOrder + superset.sortOrder,
            });
            // Link exercises to superset using new exercise IDs
            superset.exercises.forEach(({ exercise, sortOrder }) => {
                const newExerciseId = exercise.workoutExerciseId
                    ? exerciseIdMap.get(exercise.workoutExerciseId)
                    : undefined;
                if (newExerciseId) {
                    this.addSupersetExercise({
                        workoutExerciseId: newExerciseId,
                        workoutSupersetId: newSupersetId,
                        sortOrder,
                    });
                }
            });
            // Handle alternate groups within superset using the map
            const alternateGroupIds = supersetAlternateGroupMap.get(oldSupersetId) || [];
            alternateGroupIds.forEach((altGroupId) => {
                const altGroup = allAlternateGroups.find((ag) => ag.workoutAlternateGroupId === altGroupId);
                if (!altGroup)
                    return;
                this.recreateAlternateGroup({
                    alternateGroup: altGroup,
                    exerciseIdMap,
                    targetSectionId,
                    groupSortOrder: targetSortOrder + altGroup.sortOrder,
                });
            });
        });
        // Recreate all standalone alternate group structures
        onlyAlternateGroups.forEach((alternateGroup) => {
            this.recreateAlternateGroup({
                alternateGroup,
                exerciseIdMap,
                targetSectionId,
                groupSortOrder: targetSortOrder + alternateGroup.sortOrder,
            });
        });
        return exerciseIdMap;
    }
    /**
     * Recreate one alternate group under a new group id, mapping its exercises to their new ids.
     *
     * The group row is created from the first `sortOrder: 0` exercise (the base) and only once:
     * a source group carrying more than one `sortOrder: 0` exercise would otherwise produce two
     * `alternateGroupAdds` rows for a single `workoutAlternateGroupId`, which breaks the removal
     * cascades that resolve a group id to a single row.
     */
    recreateAlternateGroup({ alternateGroup, exerciseIdMap, targetSectionId, groupSortOrder, }) {
        const newAlternateGroupId = v4();
        let baseCreated = false;
        alternateGroup.exercises.forEach(({ exercise, sortOrder }) => {
            const newExerciseId = exercise.workoutExerciseId
                ? exerciseIdMap.get(exercise.workoutExerciseId)
                : undefined;
            if (!newExerciseId)
                return;
            if (sortOrder === 0 && !baseCreated) {
                // This is the selected exercise - create the alternate group
                baseCreated = true;
                this.addAlternateGroup({
                    workoutSectionId: targetSectionId,
                    workoutExerciseId: newExerciseId,
                    sortOrder: groupSortOrder,
                    workoutAlternateGroupId: newAlternateGroupId,
                });
            }
            else {
                // These are alternate exercises
                this.addAlternateExercise({
                    workoutExerciseId: newExerciseId,
                    workoutAlternateGroupId: newAlternateGroupId,
                    data: { sortOrder },
                });
            }
        });
    }
    /**
     * Copy entries to a new section (without removing from original location)
     * Supports multiple entries:
     * - Multiple standalone exercises
     * - Multiple alternate groups
     * - Multiple supersets
     * - Mixed combinations of all types
     */
    copyEntriesToSection({ entries, targetSectionId, targetSortOrder, }) {
        const entryData = this.collectEntryData(entries);
        this.addEntriesToSection({
            targetSectionId,
            targetSortOrder,
            ...entryData,
        });
        return this;
    }
    /** Move entries to a different section, removing them from their original location */
    moveEntriesIntoNewSection({ entries, targetSectionId, targetSortOrder, }) {
        const entryData = this.collectEntryData(entries);
        this.removeEntries(entryData);
        this.addEntriesToSection({
            targetSectionId,
            targetSortOrder,
            ...entryData,
        });
        return this;
    }
    /** Returns the accumulated edits as a WorkoutLayerEdit array */
    getEdits() {
        return this.edits;
    }
    /** Returns the layer with all accumulated edits merged in */
    getLayer() {
        return WorkoutLayerDomain.mergeLayerEdits(this.layer, this.edits);
    }
    // ============================================================================
    // Private Helper Methods
    // ============================================================================
    /**
     * Check if a delete edit already exists for an entity (for idempotency)
     */
    hasPendingDelete(componentName, id) {
        return this.edits.some((edit) => edit.componentName === componentName &&
            edit.action === 'delete' &&
            'id' in edit.data &&
            edit.data.id === id);
    }
    /**
     * `sortOrder: 0` marks a group's base exercise, and the removal cascades in
     * `removeAlternateGroup` / `removeSection` treat every `sortOrder !== 0` alternate as a
     * non-base one. A group with two `sortOrder: 0` alternates therefore reads as two groups,
     * which is how duplicate `alternateGroupAdds` rows get written.
     *
     * If the group would still have a base once this builder's edits are applied, place this
     * alternate after the existing ones instead of writing a second base. Alternates already
     * marked for deletion do not count: replacing a base (see `swapExercise`) removes the old
     * sortOrder 0 alternate, and the incoming one has to take its place.
     *
     * This only sees a base that is in this builder's own layer or pending edits, so it guards
     * `swapExercise` and `recreateAlternateGroup` but not the callers that build a layer-less
     * builder (`addAlternateExerciseEdits`, `addExerciseEdits`); those pass a section-relative
     * sort order rather than 0.
     */
    resolveAlternateExerciseSortOrder(workoutAlternateGroupId, sortOrder) {
        if (sortOrder !== 0) {
            return sortOrder;
        }
        const existingAlternates = [
            ...Layer(this.layer)
                .alternateExerciseAdds('workoutAlternateGroupId', workoutAlternateGroupId)
                .filter((alternate) => !this.hasPendingDelete('alternateExerciseAdds', alternate.id)),
            ...this.getPendingCreates('alternateExerciseAdds', 'workoutAlternateGroupId', workoutAlternateGroupId),
        ];
        return existingAlternates.some((alternate) => alternate.sortOrder === 0)
            ? nextSortOrder(existingAlternates)
            : sortOrder;
    }
    /**
     * Generic helper to remove an entity (from layer, pending edits, or create removal)
     * Handles the common pattern of:
     * 1. Try to delete from layer if it exists
     * 2. Try to remove from pending creates if not in layer
     * 3. Create a removal edit if neither worked
     * true if entity was handled (deleted/removed/marked for removal)
     */
    removeEntity({ componentName, entityIdField, entityIdValue, entityType, layerEntity, }) {
        if (layerEntity) {
            // Entity exists in layer, create delete edit.
            // `this.layer` is not mutated as edits accumulate, so two removal paths reaching the
            // same entity would both resolve it and emit duplicate deletes; `mergeLayerEdits`
            // throws on the second one.
            if (!this.hasPendingDelete(componentName, layerEntity.id)) {
                this.edits.push({
                    componentName,
                    action: 'delete',
                    data: { id: layerEntity.id },
                });
            }
            return true;
        }
        else if (!this.removePendingCreate(componentName, entityIdField, entityIdValue)) {
            // Not in layer or pending edits, create a removal
            this.edits.push({
                componentName: 'removals',
                action: 'create',
                data: {
                    id: v4(),
                    entityType,
                    entityId: String(entityIdValue),
                },
            });
        }
        return true;
    }
    /**
     * Helper to remove a pending create edit by component and ID field
     * true if a pending edit was removed, false otherwise
     */
    removePendingCreate(componentName, idField, idValue) {
        const index = this.edits.findIndex((e) => e.componentName === componentName &&
            e.action === 'create' &&
            idField in e.data &&
            e.data[idField] === idValue);
        if (index !== -1) {
            this.edits.splice(index, 1);
            return true;
        }
        return false;
    }
    /**
     * Helper to remove a pending create edit by component and multiple ID fields
     * Useful for composite keys like setMetrics (workoutSetId + metricId)
     * true if a pending edit was removed, false otherwise
     */
    removePendingCreateByFields(componentName, fields) {
        const index = this.edits.findIndex((e) => {
            if (e.componentName !== componentName || e.action !== 'create') {
                return false;
            }
            // Check if all fields match
            return Object.entries(fields).every(([field, value]) => field in e.data && e.data[field] === value);
        });
        if (index !== -1) {
            this.edits.splice(index, 1);
            return true;
        }
        return false;
    }
    /**
     * Helper to get all pending create edits for a component filtered by a field value
     */
    getPendingCreates(componentName, filterField, filterValue) {
        return this.edits
            .filter((e) => {
            if (e.componentName !== componentName || e.action !== 'create') {
                return false;
            }
            if (filterField && filterValue) {
                return (filterField in e.data &&
                    e.data[filterField] === filterValue);
            }
            return true;
        })
            .map((e) => e.data);
    }
}
//# sourceMappingURL=workout-layer-builder.js.map